import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { checkAuthStatus, clearAuthData } from '../utils/shared.js'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState(null)

  console.log('AuthProvider render:', { isAuthenticated, isLoading, user: !!user })

  useEffect(() => {
    async function verifyAuth() {
      setIsLoading(true)
      try {
        const { authenticated, user: userData } = await checkAuthStatus()
        if (authenticated) {
          setIsAuthenticated(true)
          setUser(userData)
        } else {
          setIsAuthenticated(false)
          setUser(null)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        setIsAuthenticated(false)
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }
    verifyAuth()
  }, [])

  const login = useCallback(async (userData) => {
    try {
      console.log('Login: Setting user state', userData)

      // Update state
      setIsAuthenticated(true)
      setUser(userData)

      console.log('Login successful:', { isAuthenticated: true, user: userData })

    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      console.log('Logout: Clearing authentication')

      // Call server-side logout to clear httpOnly cookie + clear localStorage
      await clearAuthData()

      // Update state
      setIsAuthenticated(false)
      setUser(null)

      console.log('Logout successful')

    } catch (error) {
      console.error('Logout failed:', error)
      // Still clear local state even if API call fails
      setIsAuthenticated(false)
      setUser(null)
    }
  }, [])

  const value = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  }), [isAuthenticated, isLoading, user, login, logout])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
